/**
 * Générateur — Ordonner une liste de nombres écrits sous forme de fractions ou de nombres mixtes
 * Attendu ID : 629 | Niveau : 6ème | Domaine : NC (Nombres et calculs)
 *
 * Ce générateur contient 7 EXERCICES DIDACTIQUEMENT DISTINCTS (chacun avec
 * son propre generatorKey au niveau exercice).
 *
 * Exercices couverts (voir FICHE_PEDAGO_CLUSTER16 §6.12 à §6.18) :
 *
 *   1. `ordonner_meme_denominateur`  → Ordonner. 5 fractions a_i/b même dén.
 *   2. `ordonner_meme_numerateur`    → Ordonner. 4-5 fractions a/b_i même num.
 *   3. `ordonner_droite_graduee`     → Ordonner + SVG droite graduée.
 *   4. `ordonner_liste_mixte`        → Ordonner. 5 nombres hétérogènes (L1-L6).
 *   5. `ordonner_nombres_mixtes`     → Ordonner. 4 nombres mixtes (G1-G4).
 *   6. `ordonner_intrus`             → QCM unique. Chaîne d'inégalités avec 1 fausse.
 *   7. `ordonner_intercaler`         → QCM unique. Position d'intercalation.
 *
 * Règles dures de la fiche (§2, §3) :
 *   - POOL_A, POOL_B, POOL_E sont réutilisés depuis 628 (valeurs dupliquées ici).
 *   - POOL_F : listes canoniques mixtes L1-L6
 *   - POOL_G : listes canoniques de nombres mixtes G1-G4
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou,
    basePayload, tex,
} from '../../../utils';
import { gcd, fmtFrac, fmtKatex, fmtItem } from './fraction';

/**
 * Étiquette d'un item à ordonner telle que l'élève la LIT (audit fractions
 * en texte, 2026-09-06) : les pools et le tri restent en texte (« 1/2 »,
 * « 3 + 1/4 », évalués par evalElement), l'affichage passe en KaTeX au moment
 * de construire le payload. Les items et la bonne réponse partagent la même
 * chaîne, la correction reste exacte.
 */
const itemTex = (item) => {
    const s = String(item).trim();
    const mixte = s.match(/^(\d+) \+ (\d+)\/(\d+)$/);
    if (mixte) return tex(`${mixte[1]} + \\dfrac{${mixte[2]}}{${mixte[3]}}`);
    const frac = s.match(/^(\d+)\/(\d+)$/);
    if (frac) return tex(`\\dfrac{${frac[1]}}{${frac[2]}}`);
    return s;
};

// ═══════════════════════════════════════════════════════════
// CONSTANTES & LABELS
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 629;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_ORD_MD       = 'Ranger une liste de fractions de même dénominateur';
const L_ORD_MN       = 'Ranger une liste de fractions de même numérateur';
const L_ORD_DROITE   = 'Placer sur une droite graduée et ranger';
const L_ORD_MIXTE    = 'Ranger une liste hétérogène';
const L_ORD_NB_MIXTE = 'Ranger des nombres mixtes';
const L_ORD_INTRUS   = 'Trouver l\'intrus dans une chaîne d\'inégalités';
const L_ORD_INTERCAL = 'Intercaler une fraction dans une liste triée';

// ═══════════════════════════════════════════════════════════
// HELPERS ARITHMÉTIQUES
// ═══════════════════════════════════════════════════════════

const fracCmp = (a, b, c, d) => {
    const lhs = a * d;
    const rhs = b * c;
    return lhs < rhs ? -1 : (lhs > rhs ? 1 : 0);
};

const fracEqual = (a, b, c, d) => a * d === b * c;

/** Évalue un élément (fraction "a/b", entier, nombre mixte "n + a/b") en valeur flottante. */
const evalElement = (s) => {
    s = s.trim();
    // nombre mixte "n + a/b"
    let m = s.match(/^(\d+)\s*\+\s*(\d+)\/(\d+)$/);
    if (m) {
        return Number(m[1]) + Number(m[2]) / Number(m[3]);
    }
    // fraction "a/b"
    m = s.match(/^(\d+)\/(\d+)$/);
    if (m) {
        return Number(m[1]) / Number(m[2]);
    }
    // entier
    m = s.match(/^(\d+)$/);
    if (m) {
        return Number(m[1]);
    }
    throw new Error(`evalElement: format inconnu "${s}"`);
};

/** Convertit un élément en KaTeX pour affichage. */
const elementToKatex = (s) => {
    s = s.trim();
    let m = s.match(/^(\d+)\s*\+\s*(\d+)\/(\d+)$/);
    if (m) {
        return `${m[1]} + \\dfrac{${m[2]}}{${m[3]}}`;
    }
    m = s.match(/^(\d+)\/(\d+)$/);
    if (m) {
        return `\\dfrac{${m[1]}}{${m[2]}}`;
    }
    return s;
};

// ═══════════════════════════════════════════════════════════
// POOL_A — Couples de fractions de même dénominateur (réutilisé de 628)
// ═══════════════════════════════════════════════════════════

const POOL_A = {
    2:  [1, 3, 5, 7],
    3:  [1, 2, 4, 5, 7, 8],
    4:  [1, 3, 5, 7, 9, 11],
    5:  [1, 2, 3, 4, 6, 7, 8, 9],
    6:  [1, 5, 7, 11],
    7:  [1, 2, 3, 4, 5, 6, 8, 9, 10],
    8:  [1, 3, 5, 7, 9, 11, 13],
    9:  [1, 2, 4, 5, 7, 8, 10],
    10: [1, 3, 7, 9, 11, 13],
    11: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12],
    12: [1, 5, 7, 11, 13],
};

const POOL_B = {
    1: [2, 3, 4, 5, 6, 7, 8, 9, 10, 12],
    2: [3, 5, 7, 9, 11],
    3: [4, 5, 7, 8, 10, 11],
    4: [5, 7, 9, 11, 13],
    5: [6, 7, 8, 9, 11, 12],
    7: [3, 8, 9, 11, 12],
};

// ═══════════════════════════════════════════════════════════
// POOL_F — Listes canoniques mixtes (L1-L6)
// ═══════════════════════════════════════════════════════════

/**
 * Chaque liste est un tableau d'items déjà triés dans l'ordre croissant.
 * Les items sont des chaînes : "a/b", "n", ou "n + a/b".
 */
const POOL_F = [
    // L1 : liste canonique du cours
    ['99/100', '1', '7/6', '1 + 1/3', '5/3'],
    // L2 : dénominateur partiel + nombre mixte
    ['2', '7/3', '8/3', '2 + 3/4', '3'],
    // L3 : tous < 1, comparaison à 1/2 + même dén partiel
    ['1/4', '3/8', '1/2', '5/8', '7/8'],
    // L4 : comparaison à 1/2 autour (tous 4 < 1)
    ['2/5', '1/2', '3/5', '2/3', '3/4'],
    // L5 : encadrement + nombre mixte
    ['3', '10/3', '7/2', '3 + 3/4', '4'],
    // L6 : série de Farey simples
    ['1/2', '2/3', '3/4', '4/5', '5/6'],
];

// Vérification runtime : chaque liste est strictement croissante.
for (let i = 0; i < POOL_F.length; i++) {
    const liste = POOL_F[i];
    for (let j = 1; j < liste.length; j++) {
        const v1 = evalElement(liste[j - 1]);
        const v2 = evalElement(liste[j]);
        if (v1 >= v2) {
            throw new Error(`POOL_F[${i}]: ordre non strict ${liste[j - 1]} >= ${liste[j]}`);
        }
    }
}

// ═══════════════════════════════════════════════════════════
// POOL_G — Listes canoniques de nombres mixtes (G1-G4)
// ═══════════════════════════════════════════════════════════

const POOL_G = [
    // G1 : 4 nombres mixtes variés
    ['1 + 3/4', '2 + 1/4', '2 + 3/5', '3 + 1/2'],
    // G2 : même partie entière 1, dén différents mais un divisant l'autre (3 et 6)
    ['1 + 1/6', '1 + 1/3', '1 + 1/2', '1 + 5/6'],
    // G3 : même dén 5, parties entières 2 et 3 mélangées
    ['2 + 1/5', '2 + 4/5', '3 + 1/5', '3 + 2/5'],
    // G4 : mélange de parties entières 1 et 2 avec dén 2 et 4
    ['1 + 1/2', '1 + 3/4', '2 + 1/4', '2 + 1/2'],
];

for (let i = 0; i < POOL_G.length; i++) {
    const liste = POOL_G[i];
    for (let j = 1; j < liste.length; j++) {
        const v1 = evalElement(liste[j - 1]);
        const v2 = evalElement(liste[j]);
        if (v1 >= v2) {
            throw new Error(`POOL_G[${i}]: ordre non strict ${liste[j - 1]} >= ${liste[j]}`);
        }
    }
}

// ═══════════════════════════════════════════════════════════
// SVG HELPER — droite graduée
// ═══════════════════════════════════════════════════════════

/**
 * Construit une droite graduée SVG en b-ièmes, de 0 à maxEnt entiers.
 * - largeur : 500 px
 * - graduations principales sur les entiers (épaisses + étiquettes)
 * - graduations secondaires sur les (b-1) sous-divisions par unité entière
 */
const SVG_STROKE = 'currentColor';
const SVG_TEXT = 'currentColor';
const SVG_MINOR = '#94a3b8';

const buildDroiteGradueeSvg = (b, maxEnt) => {
    const width = 500;
    const marginL = 30;
    const marginR = 30;
    const y = 40;
    const totalSub = maxEnt * b;
    const dx = (width - marginL - marginR) / totalSub;
    const parts = [];
    parts.push(`<svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='80' viewBox='0 0 ${width} 80'>`);
    // Ligne principale
    parts.push(`<line x1='${marginL}' y1='${y}' x2='${width - marginR}' y2='${y}' stroke='${SVG_STROKE}' stroke-width='2'/>`);
    // Flèche à droite
    parts.push(`<polygon points='${width - marginR},${y} ${width - marginR - 10},${y - 5} ${width - marginR - 10},${y + 5}' fill='${SVG_STROKE}'/>`);
    // Graduations
    for (let i = 0; i <= totalSub; i++) {
        const x = marginL + i * dx;
        const isEntier = i % b === 0;
        const yTop = isEntier ? y - 8 : y - 5;
        const yBot = isEntier ? y + 8 : y + 5;
        const sw = isEntier ? 2 : 1;
        const color = isEntier ? SVG_STROKE : SVG_MINOR;
        parts.push(`<line x1='${x}' y1='${yTop}' x2='${x}' y2='${yBot}' stroke='${color}' stroke-width='${sw}'/>`);
        if (isEntier) {
            const val = i / b;
            parts.push(`<text x='${x}' y='${y + 25}' font-family='sans-serif' font-size='14' text-anchor='middle' fill='${SVG_TEXT}'>${val}</text>`);
        }
    }
    parts.push('</svg>');
    const svg = parts.join('');
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — ordonner_meme_denominateur
// ═══════════════════════════════════════════════════════════

const genOrdonnerMemeDenominateur = () => {
    // Tire un dén b qui a au moins 5 numérateurs disponibles.
    const bChoices = Object.keys(POOL_A).map(Number).filter(b => POOL_A[b].length >= 5);
    const b = randomChoice(bChoices);
    const nums = shuffle(POOL_A[b]).slice(0, 5).sort((a, c) => a - c);
    const items = nums.map(a => fmtItem(a, b));
    const croissant = Math.random() < 0.5;
    const itemsTries = croissant ? items : [...items].reverse();
    const sens = croissant ? 'croissant' : 'décroissant';
    const texte = `<p>Range ces fractions dans l'ordre ${sens}.</p>`;
    const payload = basePayload('association', texte, {
        subType: 'ordonner',
        items: itemsTries,
        bonne_reponse: JSON.stringify(itemsTries),
    }, opts(L_ORD_MD));
    payload._meta.generatorKey = 'ordonner_meme_denominateur';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 — ordonner_meme_numerateur
// ═══════════════════════════════════════════════════════════

const genOrdonnerMemeNumerateur = () => {
    const aChoices = Object.keys(POOL_B).map(Number).filter(a => POOL_B[a].length >= 4);
    const a = randomChoice(aChoices);
    const dens = shuffle(POOL_B[a]).slice(0, 4).sort((x, y) => x - y);
    // Ordre croissant des fractions = ordre décroissant des dén.
    // On affiche en ordre croissant par défaut.
    const items = dens.slice().reverse().map(b => fmtItem(a, b));
    const croissant = Math.random() < 0.5;
    const itemsTries = croissant ? items : [...items].reverse();
    const sens = croissant ? 'croissant' : 'décroissant';
    const texte = `<p>Range ces fractions dans l'ordre ${sens}. Attention, elles ont le même numérateur !</p>`;
    const payload = basePayload('association', texte, {
        subType: 'ordonner',
        items: itemsTries,
        bonne_reponse: JSON.stringify(itemsTries),
    }, opts(L_ORD_MN));
    payload._meta.generatorKey = 'ordonner_meme_numerateur';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 — ordonner_droite_graduee
// ═══════════════════════════════════════════════════════════

const genOrdonnerDroiteGraduee = () => {
    // Tire un dénominateur b ∈ [3, 6] (pour que la droite soit lisible)
    const b = randomChoice([3, 4, 5, 6]);
    // Max entier 2 ou 3 : la droite va de 0 à 3 (ou 2) avec pas commun
    const maxEnt = randomChoice([2, 3]);
    const totalSub = maxEnt * b;
    // Tire 5 numérateurs distincts dans [1, totalSub] (fraction doit être > 0 et ≤ maxEnt)
    const nums = [];
    const pool = [];
    for (let i = 1; i <= totalSub; i++) pool.push(i);
    const picks = shuffle(pool).slice(0, 5).sort((x, y) => x - y);
    for (const n of picks) nums.push(n);
    const items = nums.map(n => fmtItem(n, b));
    // Ordre croissant
    const svg = buildDroiteGradueeSvg(b, maxEnt);
    const texte = `<p>Voici une droite graduée. Range ces fractions dans l'ordre croissant en t'aidant de la droite.</p>{{element:0}}`;
    const payload = basePayload('association', texte, {
        subType: 'ordonner',
        items,
        bonne_reponse: JSON.stringify(items),
    }, opts(L_ORD_DROITE), {
        version: 1,
        elements: [
            { type: 'image', config: { imageUrl: svg } },
        ],
    });
    payload._meta.generatorKey = 'ordonner_droite_graduee';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 4 — ordonner_liste_mixte
// ═══════════════════════════════════════════════════════════

// Fractions pédagogiquement "comparables via repère 1 ou 1/2"
const FRACTIONS_FAREY = ['1/2', '2/3', '3/4', '4/5', '5/6', '6/7', '7/8', '8/9', '9/10'];
const FRACTIONS_INVERSE = ['1/2', '1/3', '1/4', '1/5', '1/6', '1/7', '1/8', '1/9', '1/10'];

/** Trie une liste d'items (fractions/entiers/mixtes) par valeur numérique. */
const sortItemsByValue = (items) => {
    const arr = items.slice();
    arr.sort((a, b) => evalElement(a) - evalElement(b));
    return arr;
};

/** Génère dynamiquement une liste hétérogène triée selon 5 stratégies pédagogiques. */
const genListeMixteDynamique = () => {
    const strategie = randomChoice(['A', 'B', 'C', 'D', 'E']);
    let items;
    if (strategie === 'A') {
        // Farey pure : que des (b-1)/b, repère à 1
        items = randomChoiceMultiple(FRACTIONS_FAREY, randInt(4, 5));
    } else if (strategie === 'B') {
        // 1/b inverse : que des 1/b, même numérateur
        items = randomChoiceMultiple(FRACTIONS_INVERSE, randInt(4, 5));
    } else if (strategie === 'C') {
        // Autour de 1/2 : 1-2 items < 1/2 + 1/2 + 1-2 items > 1/2
        const petits = randomChoiceMultiple(FRACTIONS_INVERSE.slice(1), randInt(1, 2));
        const grands = randomChoiceMultiple(FRACTIONS_FAREY.slice(1), randInt(1, 2));
        items = [...petits, '1/2', ...grands];
    } else if (strategie === 'D') {
        // Avec entiers/bornes : n, fraction > 1 → mixte, n+1
        const n = randInt(1, 4);
        const fracs = randomChoiceMultiple([...FRACTIONS_FAREY.slice(1), ...FRACTIONS_INVERSE.slice(1)], 3);
        items = [String(n), ...fracs.map(f => `${n} + ${f}`), String(n + 1)];
    } else {
        // Mixtes même entier
        const n = randInt(1, 4);
        const pool = [...new Set([...FRACTIONS_FAREY, ...FRACTIONS_INVERSE])];
        const fracs = randomChoiceMultiple(pool, randInt(4, 5));
        items = fracs.map(f => `${n} + ${f}`);
    }
    const sorted = sortItemsByValue(items);
    // Vérification : pas de doublons de valeur (1/2 et 1/2 éventuels après déduplication pool)
    for (let i = 1; i < sorted.length; i++) {
        if (evalElement(sorted[i - 1]) >= evalElement(sorted[i])) return null;
    }
    return sorted;
};

const genOrdonnerListeMixte = () => {
    const liste = genListeMixteDynamique() || randomChoice(POOL_F);
    const items = liste.map(itemTex);
    const texte = `<p>Range dans l'ordre croissant les nombres suivants.</p>`;
    const payload = basePayload('association', texte, {
        subType: 'ordonner',
        items,
        bonne_reponse: JSON.stringify(items),
    }, opts(L_ORD_MIXTE));
    payload._meta.generatorKey = 'ordonner_liste_mixte';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 5 — ordonner_nombres_mixtes
// ═══════════════════════════════════════════════════════════

/**
 * Génère dynamiquement 4 nombres mixtes ordonnés.
 * 50% même partie entière (force comparaison fractionnaire via repère 1 ou 1/2),
 * 50% parties entières différentes (force comparaison entiers d'abord).
 */
const genNombresMixtesDynamique = () => {
    const memeEntier = Math.random() < 0.5;
    const pool = [...new Set([...FRACTIONS_FAREY, ...FRACTIONS_INVERSE])];
    let items;
    if (memeEntier) {
        const n = randInt(1, 4);
        const fracs = randomChoiceMultiple(pool, 4);
        items = fracs.map(f => `${n} + ${f}`);
    } else {
        // 2 entiers distincts parmi [1, 4], 2 fractions par entier
        const n1 = randInt(1, 3);
        const n2 = n1 + randInt(1, 2);
        const fracs1 = randomChoiceMultiple(pool, 2);
        const fracs2 = randomChoiceMultiple(pool, 2);
        items = [
            ...fracs1.map(f => `${n1} + ${f}`),
            ...fracs2.map(f => `${n2} + ${f}`),
        ];
    }
    const sorted = sortItemsByValue(items);
    // Vérification strictement croissant
    for (let i = 1; i < sorted.length; i++) {
        if (evalElement(sorted[i - 1]) >= evalElement(sorted[i])) return null;
    }
    return sorted;
};

const genOrdonnerNombresMixtes = () => {
    const liste = genNombresMixtesDynamique() || randomChoice(POOL_G);
    const items = liste.map(itemTex);
    const texte = `<p>Range ces nombres mixtes dans l'ordre croissant.</p>`;
    const payload = basePayload('association', texte, {
        subType: 'ordonner',
        items,
        bonne_reponse: JSON.stringify(items),
    }, opts(L_ORD_NB_MIXTE));
    payload._meta.generatorKey = 'ordonner_nombres_mixtes';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 6 — ordonner_intrus
// ═══════════════════════════════════════════════════════════

const genOrdonnerIntrus = () => {
    // Construit une chaîne de 4 fractions rangées correctement puis remplace UN maillon
    // par une inégalité fausse obtenue en inversant deux voisines OU en introduisant
    // un faux couple même dén / num.
    // Stratégie : tirer 4 fractions distinctes, les trier strictement, obtenir une chaîne
    // c1 < c2 < c3 < c4 (3 maillons vrais), puis remplacer un maillon par
    // son inverse « ci+1 < ci » (maillon faux).
    //
    // Plus pédagogique : on prend 4 fractions dont les comparaisons 2-à-2 sont faciles
    // (même dén, même num, comparaison à 1/2) et on inverse juste le maillon à détruire.
    const b = randomChoice([6, 7, 8, 9, 10, 11, 12]);
    const candidats = POOL_A[b] || [];
    if (candidats.length < 4) throw new Error('ordonner_intrus: pas assez de candidats');
    const nums = shuffle(candidats).slice(0, 4).sort((x, y) => x - y);
    // Chaîne vraie : n1/b < n2/b < n3/b < n4/b
    const items = nums.map(n => fmtKatex(n, b));
    // 3 maillons vrais (wrapped in $...$ for KaTeX display in QCM choices)
    const maillonsVrais = [
        `$${items[0]} < ${items[1]}$`,
        `$${items[1]} < ${items[2]}$`,
        `$${items[2]} < ${items[3]}$`,
    ];
    // On va remplacer UN maillon par une inégalité fausse (l'inverser)
    const idx = randInt(0, 2);
    const faux = idx === 0 ? `$${items[1]} < ${items[0]}$`
              : idx === 1 ? `$${items[2]} < ${items[1]}$`
              : `$${items[3]} < ${items[2]}$`;
    const maillonsAffiches = maillonsVrais.slice();
    maillonsAffiches[idx] = faux;
    // Construire l'affichage KaTeX de la chaîne
    const chaineKatex = idx === 0
        ? `${fmtKatex(nums[1], b)} < ${fmtKatex(nums[0], b)} < ${fmtKatex(nums[2], b)} < ${fmtKatex(nums[3], b)}`
        : idx === 1
        ? `${fmtKatex(nums[0], b)} < ${fmtKatex(nums[2], b)} < ${fmtKatex(nums[1], b)} < ${fmtKatex(nums[3], b)}`
        : `${fmtKatex(nums[0], b)} < ${fmtKatex(nums[1], b)} < ${fmtKatex(nums[3], b)} < ${fmtKatex(nums[2], b)}`;
    const texte = `<p>Une des inégalités de cette chaîne est fausse. Laquelle ?</p>{{element:0}}`;
    const choix = maillonsAffiches;
    const bonne = faux;
    const payload = makeQcm(choix, bonne, texte, opts(L_ORD_INTRUS));
    payload.elements_additionnels = {
        version: 1,
        elements: [
            { type: 'expression', config: { contenu: chaineKatex } },
        ],
    };
    payload._meta.generatorKey = 'ordonner_intrus';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 7 — ordonner_intercaler
// ═══════════════════════════════════════════════════════════

const genOrdonnerIntercaler = () => {
    // Tire un dénominateur b, 5 fractions triées de dénominateur b, puis
    // une fraction x = k/b avec k strictement entre deux voisines.
    const bChoices = Object.keys(POOL_A).map(Number).filter(b => POOL_A[b].length >= 6);
    const b = randomChoice(bChoices);
    // Tire 5 numérateurs + 1 num à intercaler strictement entre deux
    const allNums = POOL_A[b];
    let chosen, xNum, idxInsert;
    for (let attempt = 0; attempt < 50; attempt++) {
        const nums = shuffle(allNums).slice(0, 5).sort((x, y) => x - y);
        // Cherche une position interne où un numérateur strictement intercalé existe
        const positions = [];
        for (let i = 0; i < 4; i++) {
            // entre nums[i] et nums[i+1] : au moins un entier strictement entre les deux
            if (nums[i + 1] - nums[i] >= 2) {
                positions.push(i);
            }
        }
        if (positions.length === 0) continue;
        const idx = randomChoice(positions);
        // Trouver un numérateur strict entre nums[idx] et nums[idx+1]
        const candidates = [];
        for (let k = nums[idx] + 1; k < nums[idx + 1]; k++) {
            if (!nums.includes(k)) candidates.push(k);
        }
        if (candidates.length === 0) continue;
        xNum = randomChoice(candidates);
        chosen = nums;
        idxInsert = idx;
        break;
    }
    if (!chosen) throw new Error('ordonner_intercaler: aucun candidat trouvé');
    // Construire la liste triée affichée
    const listeKatex = chosen.map(n => fmtKatex(n, b)).join(' < ');
    // Les 4 options d'intercalation
    const choix = [];
    for (let i = 0; i < 4; i++) {
        choix.push(`Entre ${fmtItem(chosen[i], b)} et ${fmtItem(chosen[i + 1], b)}`);
    }
    const bonne = choix[idxInsert];
    const texte = `<p>Entre quelles deux fractions faut-il placer {{element:1}} dans la liste rangée ?</p>{{element:0}}`;
    const payload = makeQcm(choix, bonne, texte, opts(L_ORD_INTERCAL));
    payload.elements_additionnels = {
        version: 1,
        elements: [
            { type: 'expression', config: { contenu: `\\text{Liste : } ${listeKatex}` } },
            { type: 'expression', config: { contenu: fmtKatex(xNum, b) } },
        ],
    };
    payload._meta.generatorKey = 'ordonner_intercaler';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    ordonner_meme_denominateur_ordonner: genOrdonnerMemeDenominateur,
    ordonner_meme_numerateur_ordonner:   genOrdonnerMemeNumerateur,
    ordonner_droite_graduee_ordonner:    genOrdonnerDroiteGraduee,
    ordonner_liste_mixte_ordonner:       genOrdonnerListeMixte,
    ordonner_nombres_mixtes_ordonner:    genOrdonnerNombresMixtes,
    ordonner_intrus_qcm_unique:          genOrdonnerIntrus,
    ordonner_intercaler_qcm_unique:      genOrdonnerIntercaler,
};

// ═══════════════════════════════════════════════════════════
// CATEGORIES
// ═══════════════════════════════════════════════════════════

const FORMAT_LABELS = {
    qcm_unique: 'QCM',
    texte: 'Saisie',
    ordonner: 'Ordonner',
    texte_trou_saisie: 'Saisie trous',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'ordonner_meme_denominateur', label: 'Ordonner : même dénominateur', formatsList: buildFormats('ordonner_meme_denominateur') },
    { id: 'ordonner_meme_numerateur',   label: 'Ordonner : même numérateur',   formatsList: buildFormats('ordonner_meme_numerateur') },
    { id: 'ordonner_droite_graduee',    label: 'Placer sur droite graduée',    formatsList: buildFormats('ordonner_droite_graduee') },
    { id: 'ordonner_liste_mixte',       label: 'Ordonner une liste mixte',     formatsList: buildFormats('ordonner_liste_mixte') },
    { id: 'ordonner_nombres_mixtes',    label: 'Ordonner des nombres mixtes',  formatsList: buildFormats('ordonner_nombres_mixtes') },
    { id: 'ordonner_intrus',            label: 'Trouver l\'intrus',            formatsList: buildFormats('ordonner_intrus') },
    { id: 'ordonner_intercaler',        label: 'Intercaler une fraction',      formatsList: buildFormats('ordonner_intercaler') },
];

// ═══════════════════════════════════════════════════════════
// ENTRÉE PRINCIPALE
// ═══════════════════════════════════════════════════════════

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Toujours forcer la clé complète (les générateurs ne posent que le préfixe
    // catégorie ce qui cassait le "regenerate dans le même format")
    payload._meta.generatorKey = key;
    return payload;
};

// Exports internes pour les tests
export const _internals = {
    POOL_A, POOL_B, POOL_F, POOL_G,
    GENERATORS, CATEGORIES,
    gcd, fracCmp, fracEqual, evalElement, elementToKatex,
    fmtFrac, fmtKatex, fmtItem,
    buildDroiteGradueeSvg,
};
